import { nextKeys, prevKeys, outOfView } from "../../shared/utils";
import { postData } from "../../shared/utils";
import { MENTION_CHIP_CLASS } from "../../lib/textRendering";

export function commentEditor(options = {}) {
  const {
    mode = "create", // 'create-comment', 'edit-comment', or 'edit-response'
    commentId = null, // Required if mode='edit-comment'
    initialText = "", // Required if mode='edit-comment' or 'edit-response'
    responseId = null, // Required for API calls
    textVisibleTo = null, // Required if mode='edit-response'
    allowMentions = true, // Whether to enable @ mention functionality
  } = options;

  return {
    // Configuration
    mode,
    commentId,
    responseId,
    initialText,
    textVisibleTo,
    allowMentions,

    // State
    showDropdown: false,
    searchTerm: "",
    searchResults: [],
    focusedResult: null,
    mentionStartPos: null,
    hiddenTextarea: null, // Store reference to avoid re-querying
    dropdownStyle: {}, // Dynamic positioning for fixed dropdown
    isLoading: false, // API call in progress

    // User cache for mentions in this comment
    userCache: new Map(),

    async init() {
      // Dispatch editor opened event
      this.$dispatch("editor-opened", {
        commentId: this.commentId,
        mode: this.mode,
      });

      // Find and store the textarea reference once during init
      this.hiddenTextarea = this.$el.parentElement?.querySelector(
        "textarea.mention-hidden-textarea",
      );

      if (!this.hiddenTextarea) {
        console.error(
          "commentEditor: Could not find hidden textarea. Please ensure the textarea.mention-hidden-textarea element exists as a sibling.",
        );
        return;
      }

      // If we have initial text (edit mode), parse and render it
      if (this.initialText && this.initialText.trim()) {
        await this.initializeWithText(this.initialText);
      } else {
        this.hiddenTextarea.value = "";
      }

      // Focus the editor and place cursor appropriately
      this.$nextTick(() => {
        const editor = this.$refs.editor;
        if (!editor) return;

        editor.focus();

        // For edit modes, place cursor at end of content
        if (this.mode === "edit-comment" || this.mode === "edit-response") {
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(editor);
          range.collapse(false); // Collapse to end
          selection.removeAllRanges();
          selection.addRange(range);
        }
      });
    },

    // Initialize editor with existing text containing mention tokens
    async initializeWithText(text) {
      if (!window.parseMentionEmails || !window.userSearch) {
        console.error(
          "Required globals (parseMentionEmails, userSearch) are not available",
        );
        // Fallback: set plain text in editor
        this.$refs.editor.textContent = text;
        this.syncToHiddenField();
        return;
      }

      // Parse mention tokens from text
      const emails = window.parseMentionEmails(text);

      // Fetch user data for all mentioned emails
      await Promise.all(
        Array.from(emails).map(async (email) => {
          try {
            const user = await window.userSearch.findByEmail(email);
            if (user) {
              this.userCache.set(email, user);
            }
          } catch (error) {
            console.error(`Error fetching user for ${email}:`, error);
          }
        }),
      );

      // Convert text with tokens to HTML with mention chips
      const html = this.renderTextWithMentions(text, this.userCache);

      // Set content in the editor
      this.$refs.editor.innerHTML = html;

      // Sync to hidden field
      this.syncToHiddenField();
    },

    // Render text with mention tokens as HTML with mention chips
    renderTextWithMentions(text, userCache) {
      if (!text) return "";

      // Escape HTML but keep newlines
      const escapeHtml = (str) => {
        const div = document.createElement("div");
        div.textContent = str;
        return div.innerHTML;
      };

      // Replace mention tokens with mention chip HTML
      const mentionPattern = /\{\{mention:([^}]+)\}\}/g;
      let lastIndex = 0;
      let result = "";

      let match;
      while ((match = mentionPattern.exec(text)) !== null) {
        // Add text before this mention
        const beforeText = text.substring(lastIndex, match.index);
        result += escapeHtml(beforeText);

        // Add mention chip
        const email = match[1];
        const user = userCache.get(email);
        const displayName = user ? user.name : email;
        result += `<span class="${MENTION_CHIP_CLASS}" data-mention data-email="${escapeHtml(email)}" contenteditable="false">@${escapeHtml(displayName)}</span>`;

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text after last mention
      result += escapeHtml(text.substring(lastIndex));

      // Convert newlines to <br> tags
      result = result.replace(/\n/g, "<br>");

      return result;
    },

    // Get the hidden textarea (returns stored reference)
    getHiddenTextarea() {
      return this.hiddenTextarea;
    },

    // Handle input in the contenteditable div
    handleInput() {
      // Sync the raw text to hidden textarea
      this.syncToHiddenField();

      // Check if we should show the mention dropdown (only if mentions enabled)
      if (this.allowMentions) {
        this.checkForMentionTrigger();
      }
    },

    // Sync contenteditable content to hidden textarea with tokens
    syncToHiddenField() {
      const editor = this.$refs.editor;
      const textarea = this.getHiddenTextarea();
      if (!textarea) {
        return;
      }

      let rawText = "";

      // Walk through all child nodes and extract text + mention tokens
      editor.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          rawText += node.textContent;
        } else if (
          node.nodeType === Node.ELEMENT_NODE &&
          node.hasAttribute("data-mention")
        ) {
          const email = node.getAttribute("data-email");
          rawText += `{{mention:${email}}}`;
        } else if (node.nodeName === "BR") {
          rawText += "\n";
        } else if (node.nodeName === "DIV") {
          // Handle line breaks from Enter key
          if (rawText && !rawText.endsWith("\n")) {
            rawText += "\n";
          }
          // Recursively process child nodes
          node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
              rawText += child.textContent;
            } else if (
              child.nodeType === Node.ELEMENT_NODE &&
              child.hasAttribute("data-mention")
            ) {
              const email = child.getAttribute("data-email");
              rawText += `{{mention:${email}}}`;
            }
          });
        }
      });
      textarea.value = rawText;
    },

    // Check if user typed @ to trigger mention dropdown
    checkForMentionTrigger() {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      const node = range.startContainer;

      // Only trigger in text nodes
      if (node.nodeType !== Node.TEXT_NODE) {
        if (this.showDropdown) this.hideDropdown();
        return;
      }

      const text = node.textContent;
      const cursorPos = range.startOffset;

      // Look backwards from cursor to find @
      let atPos = -1;
      for (let i = cursorPos - 1; i >= 0; i--) {
        const char = text[i];
        if (char === "@") {
          atPos = i;
          break;
        }
        // Stop if we hit newline
        if (/[\n]/.test(char)) break;
      }

      if (atPos >= 0) {
        // Found @ - extract search term
        this.searchTerm = text.substring(atPos + 1, cursorPos);
        this.mentionStartPos = { node, offset: atPos };
        this.showMentionDropdown();
      } else if (this.showDropdown) {
        this.hideDropdown();
      }
    },

    // Show the mention dropdown and search for users
    async showMentionDropdown() {
      this.showDropdown = true;
      this.positionDropdown();

      try {
        const results = await window.userSearch.search(this.searchTerm, []);
        const term = this.searchTerm.toLowerCase();

        // Sort by relevance: exact match > starts with > contains, then by name length
        const sortedResults = results.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          const aExact = aName === term;
          const bExact = bName === term;
          if (aExact !== bExact) return aExact ? -1 : 1;

          const aStarts = aName.startsWith(term);
          const bStarts = bName.startsWith(term);
          if (aStarts !== bStarts) return aStarts ? -1 : 1;

          return aName.length - bName.length;
        });

        this.searchResults = sortedResults.slice(0, 10);
        this.focusedResult = this.searchResults[0] || null;

        // Reposition after results load in case height changes
        this.$nextTick(() => {
          this.positionDropdown();
        });
      } catch (error) {
        console.error("Error searching users:", error);
        this.searchResults = [];
      }
    },

    hideDropdown() {
      this.showDropdown = false;
      this.searchResults = [];
      this.focusedResult = null;
      this.mentionStartPos = null;
    },

    // Position dropdown relative to viewport using fixed positioning
    positionDropdown() {
      if (!this.$refs.editor) return;

      const editorRect = this.$refs.editor.getBoundingClientRect();
      const dropdownMaxHeight = 200;
      const spaceBelow = window.innerHeight - editorRect.bottom;
      const showAbove = spaceBelow < dropdownMaxHeight + 10; // 10px buffer

      this.dropdownStyle = {
        top: showAbove
          ? `${editorRect.top - dropdownMaxHeight - 5}px`
          : `${editorRect.bottom + 5}px`,
        left: `${editorRect.left}px`,
        width: `${Math.max(editorRect.width, 250)}px`,
      };
    },

    // Handle keydown events for dropdown navigation and mention insertion
    handleKeydown(event) {
      // Prevent rich text formatting shortcuts - formatting is not supported
      if (
        (event.ctrlKey || event.metaKey) &&
        ["b", "i", "u"].includes(event.key.toLowerCase())
      ) {
        event.preventDefault();
        return;
      }

      // Always intercept Escape key to prevent modal from closing
      // and losing unsaved comments
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        // If dropdown is open, hide it
        if (this.allowMentions && this.showDropdown) {
          this.hideDropdown();
        }
        return;
      }

      // Handle mention dropdown navigation (only if mentions enabled)
      if (this.allowMentions && this.showDropdown) {
        if (event.key === "Enter" && this.focusedResult) {
          event.preventDefault();
          this.insertMention(this.focusedResult);
          return;
        }

        if (event.key === "Tab" && this.focusedResult) {
          event.preventDefault();
          this.insertMention(this.focusedResult);
          return;
        }

        if (nextKeys.includes(event.key)) {
          event.preventDefault();
          this.focusNext();
          return;
        }

        if (prevKeys.includes(event.key)) {
          event.preventDefault();
          this.focusPrev();
          return;
        }
      }

      // Handle Enter when dropdown is closed - insert <br> instead of <div>
      if (event.key === "Enter" && !this.showDropdown) {
        event.preventDefault();
        document.execCommand("insertLineBreak");
        return;
      }

      // Handle backspace on mention chips
      if (event.key === "Backspace") {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        if (!range.collapsed) return;

        const container = range.startContainer;
        const offset = range.startOffset;

        // Case 1: Cursor at start of text node, check previous sibling
        if (container.nodeType === Node.TEXT_NODE && offset === 0) {
          const prevSibling = container.previousSibling;
          if (
            prevSibling?.nodeType === Node.ELEMENT_NODE &&
            prevSibling.hasAttribute("data-mention")
          ) {
            event.preventDefault();
            prevSibling.remove();
            this.syncToHiddenField();
            return;
          }
        }

        // Case 2: Cursor at offset 1 in text node that starts with space after mention
        if (
          container.nodeType === Node.TEXT_NODE &&
          offset === 1 &&
          container.textContent[0] === " "
        ) {
          const prevSibling = container.previousSibling;
          if (
            prevSibling?.nodeType === Node.ELEMENT_NODE &&
            prevSibling.hasAttribute("data-mention")
          ) {
            event.preventDefault();
            prevSibling.remove();
            // Also remove the leading space
            container.textContent = container.textContent.substring(1);
            this.syncToHiddenField();
            return;
          }
        }

        // Case 3: Cursor directly after mention element (in editor, not in text node)
        if (container.nodeType === Node.ELEMENT_NODE && offset > 0) {
          const prevChild = container.childNodes[offset - 1];
          if (
            prevChild?.nodeType === Node.ELEMENT_NODE &&
            prevChild.hasAttribute("data-mention")
          ) {
            event.preventDefault();
            prevChild.remove();
            this.syncToHiddenField();
            return;
          }
        }
      }
    },

    // Insert a mention chip at the current cursor position
    insertMention(user) {
      if (!this.mentionStartPos) return;

      const { node, offset } = this.mentionStartPos;

      // Create the mention chip element
      const mentionChip = document.createElement("span");
      mentionChip.className = MENTION_CHIP_CLASS;
      mentionChip.setAttribute("data-mention", "");
      mentionChip.setAttribute("data-email", user.email);
      mentionChip.contentEditable = "false";
      mentionChip.textContent = `@${user.name}`;

      // Get the range and split the text node
      const range = document.createRange();
      const selection = window.getSelection();

      // Replace @ and search term with mention chip
      const searchLength = this.searchTerm.length + 1; // +1 for @
      const beforeText = node.textContent.substring(0, offset);
      const afterText = node.textContent.substring(offset + searchLength);

      // Create new text nodes
      const beforeNode = document.createTextNode(beforeText);
      const afterNode = document.createTextNode(" " + afterText);

      // Replace the text node with: before + chip + space + after
      const parent = node.parentNode;
      parent.insertBefore(beforeNode, node);
      parent.insertBefore(mentionChip, node);
      parent.insertBefore(afterNode, node);
      parent.removeChild(node);

      // Move cursor after the mention chip
      range.setStart(afterNode, 1);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);

      // Hide dropdown and sync
      this.hideDropdown();
      this.syncToHiddenField();
      this.$refs.editor.focus();
    },

    // Navigate to next result in dropdown
    focusNext() {
      if (!this.focusedResult || this.searchResults.length === 0) return;

      const index = this.searchResults.findIndex(
        (u) => u.id === this.focusedResult.id,
      );
      const next = this.searchResults[index + 1];

      if (next) {
        this.focusedResult = next;
        this.scrollResultIntoView(next.id);
      }
    },

    // Navigate to previous result in dropdown
    focusPrev() {
      if (!this.focusedResult || this.searchResults.length === 0) return;

      const index = this.searchResults.findIndex(
        (u) => u.id === this.focusedResult.id,
      );
      const prev = this.searchResults[index - 1];

      if (prev) {
        this.focusedResult = prev;
        this.scrollResultIntoView(prev.id);
      }
    },

    // Scroll result into view in dropdown
    scrollResultIntoView(userId) {
      this.$nextTick(() => {
        const container = this.$refs.dropdownContainer;
        const item = container?.querySelector(`[data-user-id="${userId}"]`);

        if (container && item && outOfView(container, item)) {
          container.scrollTo(0, item.offsetTop);
        }
      });
    },

    // Select user from dropdown
    selectUser(user) {
      this.focusedResult = user;
      this.insertMention(user);
    },

    // Handle paste events to strip formatting
    handlePaste(event) {
      event.preventDefault();

      const text = event.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);

      this.syncToHiddenField();
    },

    // Save the comment (routes to create or update based on mode)
    async save() {
      if (this.isLoading) return;

      if (this.mode === "create-comment") {
        await this.create();
      } else if (this.mode === "edit-comment") {
        await this.update();
      } else if (this.mode === "edit-response") {
        await this.updateResponse();
      }
    },

    // Create a new comment
    async create() {
      const textarea = this.getHiddenTextarea();
      if (!textarea) {
        console.error("Cannot create comment: textarea not found");
        return;
      }

      const text = textarea.value;
      if (!text.trim()) {
        console.error("Cannot create comment: text is empty");
        return;
      }

      this.isLoading = true;

      try {
        const data = await postData(
          `/snapshot_response/${this.responseId}/comments`,
          { text },
        );

        // Dispatch comment-saved event with the new comment data
        this.$dispatch("comment-saved", {
          comment: data.comment,
          mode: this.mode,
          responseId: this.responseId,
        });

        // Clear the editor
        this.$refs.editor.innerHTML = "";
        textarea.value = "";
        this.userCache.clear();

        // Dispatch editor-closed event
        this.$dispatch("editor-closed", {
          commentId: this.commentId,
          mode: this.mode,
          responseId: this.responseId,
        });
      } catch (error) {
        console.error("Error creating comment:", error);
      } finally {
        this.isLoading = false;
      }
    },

    // Update an existing comment
    async update() {
      const textarea = this.getHiddenTextarea();
      if (!textarea) {
        console.error("Cannot update comment: textarea not found");
        return;
      }

      const text = textarea.value;
      if (!text.trim()) {
        console.error("Cannot update comment: text is empty");
        return;
      }

      if (!this.commentId) {
        console.error("Cannot update comment: commentId is required");
        return;
      }

      this.isLoading = true;

      try {
        await postData("/snapshot_comment/update", {
          id: this.commentId,
          text,
        });

        // Dispatch comment-saved event with the updated comment data
        this.$dispatch("comment-saved", {
          comment: { id: this.commentId, text },
          mode: this.mode,
          responseId: this.responseId,
        });

        // Dispatch editor-closed event
        this.$dispatch("editor-closed", {
          commentId: this.commentId,
          mode: this.mode,
          responseId: this.responseId,
        });
      } catch (error) {
        console.error("Error updating comment:", error);
        // TODO: Show error message to user
      } finally {
        this.isLoading = false;
      }
    },

    // Update a snapshot response
    async updateResponse() {
      const textarea = this.getHiddenTextarea();
      if (!textarea) {
        console.error("Cannot update response: textarea not found");
        return;
      }

      const text = textarea.value;
      if (!text.trim()) {
        console.error("Cannot update response: text is empty");
        return;
      }

      if (!this.responseId) {
        console.error("Cannot update response: responseId is required");
        return;
      }

      this.isLoading = true;

      try {
        await postData(`/snapshot_response/${this.responseId}`, {
          text,
          text_visible_to: this.textVisibleTo,
        });

        // Dispatch response-saved event
        this.$dispatch("response-saved", {
          responseId: this.responseId,
          text,
          mode: this.mode,
        });

        // Dispatch editor-closed event
        this.$dispatch("editor-closed", {
          commentId: this.commentId,
          mode: this.mode,
          responseId: this.responseId,
        });
      } catch (error) {
        console.error("Error updating response:", error);
      } finally {
        this.isLoading = false;
      }
    },

    // Cancel editing/creation
    cancel() {
      // Clear the editor if in create mode
      if (this.mode === "create") {
        this.$refs.editor.innerHTML = "";
        const textarea = this.getHiddenTextarea();
        if (textarea) {
          textarea.value = "";
        }
      }

      // Dispatch comment-cancelled event
      this.$dispatch("comment-cancelled", {
        commentId: this.commentId,
        mode: this.mode,
        responseId: this.responseId,
      });

      // Dispatch editor-closed event
      this.$dispatch("editor-closed", {
        commentId: this.commentId,
        mode: this.mode,
        responseId: this.responseId,
      });
    },
  };
}
